import { useTranslation } from 'react-i18next';
import { TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import RenewalSummaryPanel from '../RenewalSummaryPanel';
import ReconciliationLists from '../ReconciliationLists';
import RenewalQuotePanel from '../RenewalQuotePanel';

export { Tabs, TabsContent } from '@/components/ui/tabs';

const TABS = ['details', 'managers', 'invoices', 'quotes', 'renewal', 'lines', 'sensors', 'clause'] as const;

// Verified/ACTIVE lines only (#1407): those are what the renewal gate counts —
// ended lines never need verifying. Undefined = server predates lines: no counter.
interface SubscriptionTabsListProps {
  verifiedLines?: number;
  activeLines?: number;
}

// Eight tabs: two rows of four on small screens, one row of eight otherwise.
export function SubscriptionTabsList({ verifiedLines, activeLines }: SubscriptionTabsListProps) {
  const { t } = useTranslation('subscriptions');
  const showCounter = activeLines != null && activeLines > 0;
  return (
    <TabsList className="grid h-auto w-full grid-cols-4 sm:grid-cols-8">
      {TABS.map((tab) => (
        <TabsTrigger key={tab} value={tab}>
          {t(`tabs.${tab}`)}
          {tab === 'lines' && showCounter && (
            <span
              data-testid="lines-tab-counter"
              className={`ml-1.5 rounded-sm px-1 text-[10px] tabular-nums ${
                verifiedLines === activeLines ? 'text-success' : 'text-warning'
              }`}
              title={t('tabs.linesVerifiedCount', { count: activeLines, verified: verifiedLines, total: activeLines })}
              aria-label={t('tabs.linesVerifiedCount', { count: activeLines, verified: verifiedLines, total: activeLines })}
            >
              {verifiedLines}/{activeLines}
            </span>
          )}
        </TabsTrigger>
      ))}
    </TabsList>
  );
}

// Read-only renewal summary, the next renewal quote and the reconciliation lists.
// onGoToLines (#1407): jumps from the lines_unverified warning to the Lines tab;
// absent when the panel is rendered outside the subscription modal.
export function SubscriptionRenewalTab({
  subscriptionId,
  onGoToLines,
}: {
  subscriptionId: string | null;
  onGoToLines?: () => void;
}) {
  return (
    <TabsContent value="renewal" className="space-y-6 mt-4">
      <RenewalSummaryPanel subscriptionId={subscriptionId} />
      <RenewalQuotePanel subscriptionId={subscriptionId} onGoToLines={onGoToLines} />
      <ReconciliationLists subscriptionId={subscriptionId} />
    </TabsContent>
  );
}
